<script setup lang="ts">
/**
 * HLOS UI Shell v0.3 — Root component (HLOS-136 refator).
 *
 * **HLOS-6 (HLOS-103):** state machine de 3 estados canônicos
 * (onboarding → home ↔ conversation) — orquestrada aqui.
 *
 * **HLOS-136:** lifecycle + boot + Yjs init extraídos para
 * `useBootOrchestrator` (composable). App.vue foca apenas em:
 *   1. State machine (ref + dispatch + transition)
 *   2. Handlers `on*Complete` que delegam ao composable
 *   3. Template dos 3 states canônicos
 *
 * **Constitucional:**
 * - CL-07 (local-first): sem fetch externo; vault será plugado em HLOS-7 vault JS-side.
 * - Princípio XI (Companion ≠ LLM): copy neutra, sem "AI"/"assistant"/"inteligência".
 * - CC-01..08: zero contador/streak/nudge. Nenhuma push notification.
 * - R1-R6 (CL-06): materializado via InitiativeBadge em Home/Conversation.
 *
 * **State machine de 3 estados canônicos (Princípio V — Aprendiz Decide):**
 *   onboarding → home ↔ conversation
 */
import { defineAsyncComponent, onBeforeUnmount, onMounted, onErrorCaptured, ref } from "vue";
import {
  INITIAL_STATE,
  transition,
  type AppState,
  type AppStateEvent,
} from "./types";
import PresenceIndicator from "./presence/components/PresenceIndicator.vue";
import PrivacyFooter from "./shared/components/PrivacyFooter.vue";
import { useAuthStore } from "./auth/store";
import { useBiometricStore } from "./biometric/store";
import OnboardingState from "./states/OnboardingState.vue";
import HomeState from "./states/HomeState.vue";
import ConversationState from "./states/conversation/ConversationState.vue";
// HLOS-227 (HLOS-225.1): SettingsState renderiza <AISettingsSection>
// (disclosure UX de HLOS-225) acessível via link no HomeState footer.
import SettingsState from "./states/SettingsState.vue";
// Fase 3 Lume: CanonState (D3 — livro pessoal de compreensão).
import CanonState from "./canon/components/CanonState.vue";
// Fase 4 Lume: VaultInspector (H4 — soberania visível).
import VaultInspector from "./sovereignty/components/VaultInspector.vue";
// Fase 0 Lume: filtros SVG de daltonismo (F5) carregados uma vez no root.
import ColorblindFilters from "./accessibility/components/ColorblindFilters.vue";
import LiveAnnouncer from "./accessibility/components/LiveAnnouncer.vue";
import { useBootOrchestrator } from "./composables/useBootOrchestrator";

// HLOS-43: AuthState é lazy-loaded para evitar argon2 + BIP-39 wordlist
// no chunk inicial (mantém bundle ≤ 50KB gzipped conforme ADR-0019).
// Carregado apenas quando usuário entra na tela de auth (1ª vez ou após lock).
const AuthState = defineAsyncComponent(() => import("./auth/components/AuthState.vue"));

// Estado atual da máquina.
const state = ref<AppState>(INITIAL_STATE);

function dispatch(event: AppStateEvent): void {
  state.value = transition(state.value, event);
}

// HLOS-136: lifecycle + boot + Yjs init extraídos para composable.
const auth = useAuthStore();
// HLOS-175: biometric store — precisa de `bootHasBlob()` no startup para que
// AuthState (locked) saiba se deve mostrar o botão biometric no primeiro render
// (sem depender de a Home montar primeiro).
const biometric = useBiometricStore();
const { run, markOnboardingComplete } = useBootOrchestrator();

/**
 * HLOS-46: handler do evento `hlos:initialize` emitido pelo composable.
 * Decide INITIALIZE state baseado em session restaurada.
 */
function onInitialize(e: Event): void {
  const detail = (e as CustomEvent<{ initialState: AppState }>).detail;
  dispatch({ type: "INITIALIZE", initialState: detail.initialState });
}

onMounted(() => {
  window.addEventListener("hlos:initialize", onInitialize);
  // HLOS-175/176: carregar flag biometric de Preferences ANTES de qualquer render
  // (especialmente AuthState, que é o primeiro state a aparecer se vault locked).
  // bootHasBlob é async (Preferences.get); não bloqueia render — renderiza
  // imediatamente e re-renderiza quando hasBlob é setado.
  void biometric.bootHasBlob();
  void run();
});

onBeforeUnmount(() => {
  window.removeEventListener("hlos:initialize", onInitialize);
});

/**
 * Fase 0 (F2): anuncia mudanças de estado para leitores de tela.
 * Mapeia cada AppState para uma string descritiva, sem manipular ritmo.
 */
function announcementForState(s: AppState): string {
  switch (s) {
    case "onboarding":
      return "Tela de onboarding.";
    case "home":
      return "Tela inicial.";
    case "conversation":
      return "Em conversa.";
    case "settings":
      return "Configurações.";
    case "canon":
      return "Meu Canon.";
    case "sovereignty":
      return "Meu vault.";
    default:
      return "";
  }
}

/**
 * Fase 4 (H2): one-tap export de tudo. Stub que loga o intent.
 * Implementação completa virá quando o kernel expuser ExportPackage.
 */
function onExportAll(): void {
  // eslint-disable-next-line no-console
  console.info("[sovereignty] export requested");
  alert(
    "Exportação será processada em uma próxima versão. " +
      "Por ora, o Canon pode ser exportado como Markdown via o botão Exportar.",
  );
}

/**
 * Fase 4 (H3): one-tap delete de tudo. Stub que loga o intent.
 * Implementação completa virá quando o kernel expuser wipe completo.
 */
function onDeleteAll(): void {
  // eslint-disable-next-line no-console
  console.info("[sovereignty] delete-all requested");
  alert(
    "Apagar tudo do vault é uma operação destrutiva. " +
      "Confirmação adicional e wipe atômico virão em versão futura do kernel.",
  );
}

/**
 * HLOS-6 + HLOS-45 v0.4.3: onboarding completo → estado home + persist
 * flag `onboardingCompleted` no vault (delegado ao composable).
 */
async function onOnboardingComplete(): Promise<void> {
  dispatch({ type: "ONBOARDING_COMPLETE" });
  await markOnboardingComplete();
}

// HLOS-324: error boundary para o root. Se um subcomponente quebrar
// (ex: runtime error em ConversationChat), o root ainda renderiza
// e mostra o erro, em vez de tela branca.
const rootError = ref<string | null>(null);
onErrorCaptured((err) => {
  // eslint-disable-next-line no-console
  console.error("[HLOS][App] error captured:", err);
  rootError.value = err instanceof Error ? `${err.name}: ${err.message}` : String(err);
  // Impede propagação para o parent (Vue avisa mas não quebra a árvore)
  return false;
});
</script>

<template>
  <div class="hlos-app" :data-state="state">
  <!--
    F2 (Leitor de tela): filtros SVG para daltonismo carregados uma vez
    no root. aria-hidden porque são puramente funcionais.
  -->
  <ColorblindFilters />

  <!--
    F2: skip-link para navegação por teclado. WCAG 2.4.1 Bypass Blocks.
    Aparece no topo quando recebe foco (Tab no início da página).
  -->
  <a href="#hlos-main-content" class="skip-link">Pular para conteúdo principal</a>

  <main
    id="hlos-main-content"
    class="hlos-shell"
    tabindex="-1"
  >
    <header class="hlos-shell__header">
      <h1>HLOS</h1>
      <PresenceIndicator />
    </header>

    <!--
      HLOS-43: AuthState aparece ANTES do OnboardingState.
      Usuário precisa criar/desbloquear vault antes de usar o app.
      Transita para onboarding após dismissMnemonic() (setup) ou unlock() (locked).
    -->
    <AuthState v-if="!auth.isUnlocked" />

    <OnboardingState
      v-else-if="state === 'onboarding'"
      @complete="onOnboardingComplete"
    />

    <HomeState
      v-else-if="state === 'home'"
      @start-conversation="dispatch({ type: 'START_CONVERSATION' })"
      @open-settings="dispatch({ type: 'OPEN_SETTINGS' })"
      @open-canon="dispatch({ type: 'OPEN_CANON' })"
      @open-sovereignty="dispatch({ type: 'OPEN_SOVEREIGNTY' })"
    />

    <ConversationState
      v-else-if="state === 'conversation'"
      @end="dispatch({ type: 'END_CONVERSATION' })"
    />

    <!--
      HLOS-227 (HLOS-225.1): SettingsState renderiza <AISettingsSection>
      com disclosure UX (CL-08, Princípio XI). Usuário acessa via link no
      HomeState footer e retorna via botão "Voltar" (Princípio V).
    -->
    <SettingsState
      v-else-if="state === 'settings'"
      @close="dispatch({ type: 'CLOSE_SETTINGS' })"
    />

    <CanonState
      v-else-if="state === 'canon'"
      @close="dispatch({ type: 'CLOSE_CANON' })"
    />

    <VaultInspector
      v-else-if="state === 'sovereignty'"
      @close="dispatch({ type: 'CLOSE_SOVEREIGNTY' })"
      @export-all="onExportAll"
      @delete-all="onDeleteAll"
    />

    <!--
      HLOS-41 (HLOS-37.4): PrivacyFooter vive no nível raiz do App.vue,
      não em cada state. Garante visibilidade contínua em todas as
      transições de estado (zero CLS entre estados, zero flash de
      "footer sumiu"). Constitucional: Princípio X + XI + CC-06.
    -->
    <PrivacyFooter />

    <!--
      HLOS-319: Anuncia mudanças de estado para leitores de tela.
      aria-live="polite" para não interromper; respeita ritmo do usuário.
    -->
    <LiveAnnouncer
      :message="announcementForState(state)"
      data-testid="app-live-announcer"
    />

    <!-- HLOS-324: erro de root visível para debug -->
    <div
      v-if="rootError"
      class="hlos-app-error"
      role="alert"
      data-testid="app-root-error"
    >
      <strong>Erro no aplicativo</strong>
      <p>{{ rootError }}</p>
      <small>Reporte este problema com o texto acima.</small>
    </div>
  </main>
  </div>
</template>

<style>
:root {
  color-scheme: light dark;
  font-family:
    system-ui,
    -apple-system,
    "Segoe UI",
    Roboto,
    sans-serif;
  line-height: 1.5;
}

* {
  box-sizing: border-box;
}

body {
  margin: 0;
  padding: 0;
}

.hlos-shell {
  max-width: 720px;
  margin: 0 auto;
  padding: 1.5rem;
  min-height: 100vh;
}
.hlos-app-error {
  position: fixed;
  bottom: 1rem;
  left: 1rem;
  right: 1rem;
  max-width: 720px;
  margin: 0 auto;
  padding: 1rem;
  background: var(--hlos-color-error);
  color: var(--hlos-color-bg);
  border-radius: 8px;
  font-size: 0.85em;
  z-index: 9999;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
}
.hlos-app-error p {
  margin: 0.25rem 0;
  font-family: ui-monospace, "SF Mono", Menlo, monospace;
}
.hlos-app-error small {
  display: block;
  margin-top: 0.25rem;
  opacity: 0.85;
}

.hlos-shell__header {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 1.5rem;
}

.hlos-shell__header h1 {
  margin: 0;
  font-size: 1.25rem;
  font-weight: 600;
}
</style>
